import React from 'react';
import {DataGridPro, getGridDateOperators, getGridStringOperators} from '@mui/x-data-grid-pro';
import {Box, Typography, Avatar} from '@mui/material';

import {useTimezone} from '../../hooks/useTimezone';
import {formatDateAndTimeRange} from '../../helpers/DateTimeRangeHelper';

import ContentNameCell from './ContentNameCell';
import NoRowsOverlayLabel from "../common/NoRowsOverlayLabel";
import MemberStatusCell from '../common/MemberStatusCell';
import RecordingCell from '../common/RecordingCell';
import {retrieveServiceTypeIcon} from '../../helpers/ServiceTypeUtils';
import {buildChildBiographicalDetails, MAX_CHILDREN_TO_DISPLAY} from "../../helpers/SessionHelper";

const styles = {
    box: {
        serviceRow: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.2rem'
        },
        childInfoColumn: {
            display: 'flex',
            flexDirection: 'column',
            gap: '0.125rem',
            alignItems: 'flex-start'
        }
    },
    dataGrid: {
        maxHeight: "calc(100vh - 8.5rem)",
        "& .MuiDataGrid-cell": {
            alignItems: "center",
            backgroundColor: 'var(--mui-common-white)',
            display: "flex",
            fontSize: '0.875rem',
            lineHeight: '1.125rem !important',
            whiteSpace: 'normal',
            '& .MuiTypography-root': {
                fontSize: '0.875rem',
            },
            '&:focus': {
                outline: 'none',
            },
            '&:focus-within': {
                outline: 'none',
            },
        },
        "& .MuiDataGrid-columnHeader": {
            backgroundColor: 'var(--mui-common-white)',
            fontSize: '0.875rem',
        },
        "& .MuiDataGrid-headerFilterRow": {
            "& .MuiInputBase-input": {
                padding: '0.5rem 0.5rem',
            }
        },
        "& .MuiDataGrid-root": {
            border: 'none',
            minHeight: '12.5rem',
            minWidth: '18.75rem',
        },
        "& .MuiDataGrid-virtualScroller": {
            overflow: 'scroll !important'
        },
        "& .MuiDataGrid-row:hover": {
            backgroundColor: 'var(--mui-common-white)',
        },
        "& .MuiTablePagination-displayedRows": {
            fontSize: '0.875rem',
            letterSpacing: '0.01071em',
            lineHeight: 1.43,
        },
        "&&& .MuiDataGrid-row.Mui-selected": {
            backgroundColor: 'var(--mui-common-white)',
        },
        "&&& .MuiDataGrid-row:focus": {
            backgroundColor: 'var(--mui-common-white)',
            outline: 'none',
        },
    },
    typography: {
        date: {
            fontSize: '0.875rem',
        },
        serviceType: {
            fontSize: '0.875rem',
        },
        time: {
            fontSize: '0.875rem',
        }
    },
    avatar: {
        serviceTypeIcon: {
            width: '1.625rem',
            height: '1.625rem',
            backgroundColor: "white"
        }
    },
    icon: {
        image: {
            width: '1.25rem',
            height: '1.25rem',
        }
    }
};

export const PAGE_SIZE_OPTIONS = [25, 50, 100];

const SINGLE_LINE_ROW_HEIGHT_PIXELS = 40;
const ADDITIONAL_LINE_HEIGHT_PIXELS = 15;
const MINIMUM_DISPLAY_LINES = 1;

const calculateRowHeight = (params) => {
    const children = params.model.children || [];
    const numberOfChildrenToDisplay = children.length > MAX_CHILDREN_TO_DISPLAY ? MINIMUM_DISPLAY_LINES : children.length;
    const numberOfContentsToDisplay = params.model.contents ? params.model.contents.length : MINIMUM_DISPLAY_LINES;
    const maxLines = Math.max(numberOfChildrenToDisplay, numberOfContentsToDisplay);
    return SINGLE_LINE_ROW_HEIGHT_PIXELS + (maxLines - MINIMUM_DISPLAY_LINES) * ADDITIONAL_LINE_HEIGHT_PIXELS;
};

const MyHistorySessionsTable = ({sessions, serviceTypes, noRowsMessage, rowCount, paginationModel, onPaginationModelChange, setSortOrder, setFilterModel, isSessionDataLoading}) => {
    const {timezone} = useTimezone();

    const STRING_FILTER_OPERATORS = getGridStringOperators().filter(
        (operator) => ['contains', 'equals', 'startsWith', 'endsWith', 'isAnyOf'].includes(operator.value)
    );

    const DATE_FILTER_OPERATORS = getGridDateOperators().filter(
        (operator) => operator.value !== 'isEmpty' && operator.value !== 'isNotEmpty' && operator.value !== 'not'
    );

    const handleFilterModelChange = (newFilterModel) => {
        setFilterModel(newFilterModel);
    };

    const sessionsRows = (sessions || [])
        .map((session) => {
            if (!session) {
                return null;
            }

            const {date, time} = formatDateAndTimeRange(session.from, session.to, timezone);

            const keycodeFromSession = session.serviceType?.keycode;
            const matchingServiceType = (serviceTypes || [])
                .find(serviceType => serviceType.keycode === keycodeFromSession);

            const service = keycodeFromSession || '';
            const serviceDescription = matchingServiceType?.description || '';
            const primaryColor = matchingServiceType?.primaryColor || '';

            const contents = session.contents || [];
            const children = (session.seats || [])
                .filter(seat => !!seat.booking)
                .map(seat => seat.booking.child);

            return {
                children,
                contents,
                date,
                id: session.id,
                primaryColor,
                service,
                serviceDescription,
                session,
                time,
            };
        })
        .filter(Boolean);

    const columns = [
        {
            field: 'sessionDate',
            headerName: 'Session Date',
            renderCell: (params) => (
                <Typography variant="body2" sx={styles.typography.date}>
                    {params.row.date}
                </Typography>
            ),
            width: 190,
            filterable: true,
            filterOperators: DATE_FILTER_OPERATORS
        },
        {
            field: 'sessionTime',
            headerName: 'Session Time',
            renderCell: (params) => (
                <Typography variant="body2" sx={styles.typography.time}>
                    {params.row.time}
                </Typography>
            ),
            width: 160,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
        },
        {
            field: 'serviceType',
            headerName: 'Service',
            renderCell: (params) => {
                return (
                    <Box sx={styles.box.serviceRow}>
                        <Avatar sx={styles.avatar.serviceTypeIcon}>
                            <Box
                                component="img"
                                src={retrieveServiceTypeIcon([params.row.service])}
                                sx={styles.icon.image}/>
                        </Avatar>
                        <Typography variant="body2" sx={styles.typography.serviceType}>
                            {params.row.service}
                        </Typography>
                    </Box>
                );
            },
            filterable: true,
            filterOperators: STRING_FILTER_OPERATORS,
            sortable: false,
            width: 150,
            wrapText: true,
        },
        {
            field: 'content',
            headerName: 'Content',
            renderCell: (params) => (
                <ContentNameCell contents={params.row.contents} />
            ),
            filterable: true,
            filterOperators: STRING_FILTER_OPERATORS,
            sortable: false,
            width: 330,
            wrapText: true,
        },
        {
            field: 'parentExternalId',
            headerName: 'Member ID',
            renderCell: (params) => (
                <MemberStatusCell
                    children={params.row.children}
                    session={params.row.session}
                    timezone={timezone}
                />
            ),
            filterOperators: STRING_FILTER_OPERATORS,
            sortable: false,
            width: 150,
            wrapText: true,
        },
        {
            field: 'childInfo',
            headerName: 'Child Info',
            renderCell: (params) => {
                const children = params.row.children;
                if (children.length > MAX_CHILDREN_TO_DISPLAY) {
                    return null;
                }

                return (
                    <Box sx={styles.box.childInfoColumn}>
                        {children.map((child) => (
                            <Typography sx={{whiteSpace: 'nowrap'}}>{buildChildBiographicalDetails(child)}</Typography>
                        ))}
                    </Box>
                );
            },
            filterOperators: STRING_FILTER_OPERATORS,
            sortable: false,
            width: 200,
            wrapText: true,
        },
        {
            align: 'center',
            field: 'recording',
            headerAlign: 'center',
            headerName: 'Recording',
            renderCell: (params) => (
                <RecordingCell sessionId={params.row.id} />
            ),
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
            width: 150,
        },
    ];

    return (
        <Box sx={{minHeight: '12.5rem', minWidth: '18.75rem', width: '100%'}}>
            <DataGridPro
                headerFilters
                columns={columns}
                disableSelectionOnClick
                disableColumnPinning
                disableColumnSelector
                disableColumnReorder
                loading={isSessionDataLoading}
                getRowHeight={calculateRowHeight}
                rows={sessionsRows}
                pagination
                paginationMode="server"
                sortingMode="server"
                filterMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={onPaginationModelChange}
                onSortModelChange={setSortOrder}
                onFilterModelChange={handleFilterModelChange}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                slotProps={{
                    columnsPanel: {
                        disableHideAllButton: true,
                        disableShowAllButton: true,
                    },
                    headerFilterCell: {
                        showClearIcon: true,
                    }
                }}
                slots={{
                    noRowsOverlay: () => <NoRowsOverlayLabel message={noRowsMessage} />
                }}
                sx={{
                    ...styles.dataGrid,
                    height: 'auto',
                    minWidth: '10.75rem',
                    width: '100%',
                    "& .MuiDataGrid-footerContainer": {minHeight: SINGLE_LINE_ROW_HEIGHT_PIXELS, maxHeight: SINGLE_LINE_ROW_HEIGHT_PIXELS},
                }}
            />
        </Box>
    );
};

export default MyHistorySessionsTable;